import { UscgSymbol } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';
import type { Geometry } from 'geojson';

export type IncidentCommandPostResource = 'incidentCommandPost';
export type IncidentBase = 'incidentBase';
export type Camp = 'camp';
export type StagingArea = 'stagingArea';
export type JointInformationCenter = 'jointInformationCenter';
export type Helispot = 'helispot';
export type Helibase = 'helibase';
export type MobileRelay = 'mobileRelay';
export type OilSpill = 'oilSpill';

export const INCIDENT_COMMAND_POST_RESOURCE: IncidentCommandPostResource =
  'incidentCommandPost';
export const INCIDENT_BASE_RESOURCE: IncidentBase = 'incidentBase';
export const CAMP_RESOURCE: Camp = 'camp';
export const STAGING_AREA_RESOURCE: StagingArea = 'stagingArea';
export const JOINT_INFORMATION_CENTER_RESOURCE: JointInformationCenter =
  'jointInformationCenter';
export const HELISPOT_RESOURCE: Helispot = 'helispot';
export const HELIBASE_RESOURCE: Helibase = 'helibase';
export const MOBILE_RELAY_RESOURCE: MobileRelay = 'mobileRelay';
export const OIL_SPILL_RESOURCE: OilSpill = 'oilSpill';

export interface LocationPoint {
  id: string;
  longitude: number;
  latitude: number;
  order: number;
  createdAt: string;
}

export interface LocationLine {
  id: string;
  points: LocationPoint[];
  createdAt: string;
}

export interface SpecialLocationProperities {
  points: LocationPoint[];
  line: LocationLine | null;
}

export type BoomResourceSubType = 'proposedBoom' | 'completedBoom';
export type BoomResource = 'boom';

export const BOOM_RESOURCES = {
  COMPLETED: 'completedBoom' as const,
  PROPOSED: 'proposedBoom' as const,
} satisfies Record<string, BoomResourceSubType>;

export const BOOM_RESOURCE: BoomResource = 'boom';

export type LocationResources =
  | IncidentCommandPostResource
  | IncidentBase
  | Camp
  | StagingArea
  | JointInformationCenter
  | Helispot
  | Helibase
  | MobileRelay
  | OilSpill;
export type LocationResourcesWithBooms = LocationResources | BoomResource;

export interface LocationProperities {
  longitude: number;
  latitude: number;
}

export interface LocationPolygonProperties {
  polygonColor?: string;
  polygonOpacity?: number;
}

export interface LocationInput {
  name: string;
  resourceType: UscgSymbol;
  description?: string;
  geometry: Geometry;
  properties:
    | SpecialLocationProperities
    | LocationProperities
    | LocationPolygonProperties;
  internalLayerVersionId?: string | null;
}

export interface Location extends LocationInput {
  id: string;
  groupGuid: string;
  createdAt: string;
  updatedAt: string;
}

export type LocationUpdate = LocationInput & {
  id: string;
};

/** Type guard to check for direct latitude/longitude properties */
export const isLocationProperties = (
  properties: any
): properties is LocationProperities =>
  'latitude' in properties && 'longitude' in properties;

/** Type guard to check for points/line structure */
export const isSpecialLocationProperties = (
  properties: any
): properties is SpecialLocationProperities =>
  Array.isArray(properties.points) && properties.points.length > 0;
